import React, { useState, useEffect } from 'react';
import { invoicesService } from '../services';
import DashboardLayout from '../components/DashboardLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import InvoiceTable from '../components/InvoiceTable';
import InvoiceForm from '../components/InvoiceForm';
import InvoiceDetail from '../components/InvoiceDetail';
import Modal from '../components/Modal';
import { formatCurrency } from '../utils/formatters';
import PageHeader from '../components/ui/PageHeader';
import StatGrid from '../components/ui/StatGrid';
import FilterPanel from '../components/ui/FilterPanel';
import useConfirm from '../components/ui/useConfirm';
import Icon from '../components/nav/Icon';

const Invoices = () => {
  const [confirm, confirmDialog] = useConfirm();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [appMessage, setAppMessage] = useState({ text: '', type: '' });

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    customerPhone: '',
    startDate: '',
    endDate: ''
  });

  useEffect(() => {
    fetchInvoices();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const filterParams = {};
      if (filters.status) filterParams.status = filters.status;
      if (filters.customerPhone) filterParams.customerPhone = filters.customerPhone;
      if (filters.startDate && filters.endDate) {
        filterParams.startDate = filters.startDate;
        filterParams.endDate = filters.endDate;
      }

      const result = await invoicesService.getAll(filterParams);
      setInvoices(result || []);
    } catch (err) {
      console.error('Error fetching invoices:', err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const filterParams = {};
      if (filters.startDate) filterParams.startDate = filters.startDate;
      if (filters.endDate) filterParams.endDate = filters.endDate;

      const data = await invoicesService.getStats(filterParams);
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      customerPhone: '',
      startDate: '',
      endDate: ''
    });
  };

  const handleCreateInvoice = () => {
    setEditInvoice(null);
    setShowFormModal(true);
  };

  const handleEditInvoice = (invoice) => {
    setEditInvoice(invoice);
    setShowFormModal(true);
  };

  const handleViewInvoice = async (invoice) => {
    try {
      const fullInvoice = await invoicesService.getById(invoice.id);
      setSelectedInvoice(fullInvoice);
      setShowDetailModal(true);
    } catch (err) {
      console.error('Error loading invoice details:', err);
      showMessage('Failed to load invoice details', 'error');
    }
  };

  const handleInvoiceSaved = async () => {
    setShowFormModal(false);
    setEditInvoice(null);
    showMessage(editInvoice ? 'Invoice updated successfully' : 'Invoice created successfully', 'success');
    await fetchInvoices();
    await fetchStats();
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (await confirm('This draft invoice will be removed for good.', { title: 'Delete this invoice?', confirmText: 'Delete' })) {
      try {
        await invoicesService.delete(invoiceId);
        showMessage('Invoice deleted successfully', 'success');
        await fetchInvoices();
        await fetchStats();
      } catch (err) {
        console.error('Error deleting invoice:', err);
        showMessage(err.message || 'Failed to delete invoice', 'error');
      }
    }
  };

  const handleStatusChange = async () => {
    // Refresh the selected invoice and list
    if (selectedInvoice) {
      try {
        const updatedInvoice = await invoicesService.getById(selectedInvoice.id);
        setSelectedInvoice(updatedInvoice);
      } catch (err) {
        console.error('Error refreshing invoice:', err);
      }
    }
    await fetchInvoices();
    await fetchStats();
  };

  const showMessage = (text, type) => {
    setAppMessage({ text, type });
    setTimeout(() => setAppMessage({ text: '', type: '' }), 3000);
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'partial':
        return 'bg-blue-100 text-blue-800';
      case 'issued':
        return 'bg-yellow-100 text-yellow-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'draft':
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const activeFilters = ['status', 'startDate', 'endDate'].filter((k) => filters[k]).length;

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        <PageHeader
          title="Invoices"
          subtitle="Manage invoices and track payments"
          actions={
            <button
              type="button"
              onClick={handleCreateInvoice}
              aria-label="New invoice"
              className="h-10 w-10 md:w-auto md:px-4 inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700"
            >
              <Icon name="plus" className="w-5 h-5" />
              <span className="hidden md:inline">New invoice</span>
            </button>
          }
        />

        {appMessage.text && (
          <div role="status" className={`p-3 rounded-xl text-sm font-medium ${appMessage.type === 'success' ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
            {appMessage.text}
          </div>
        )}

        {stats && (
          <StatGrid
            items={[
              { label: 'Invoiced', value: formatCurrency(stats.totalInvoiced), sub: `${stats.totalCount} invoices` },
              { label: 'Paid', value: formatCurrency(stats.totalPaid), sub: `${stats.countByStatus?.paid || 0} paid`, tone: 'good' },
              { label: 'Outstanding', value: formatCurrency(stats.totalOutstanding), sub: `${(stats.countByStatus?.issued || 0) + (stats.countByStatus?.partial || 0)} unpaid`, tone: 'warn' },
              { label: 'Overdue', value: formatCurrency(stats.totalOverdue), sub: `${stats.countByStatus?.overdue || 0} overdue`, tone: 'bad' },
            ]}
          />
        )}

        <FilterPanel
          activeCount={activeFilters}
          columns="lg:grid-cols-4"
          search={
            <input
              type="search"
              name="customerPhone"
              aria-label="Customer phone"
              value={filters.customerPhone}
              onChange={handleFilterChange}
              placeholder="Search by phone"
              className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          }
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select name="status" value={filters.status} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="issued">Issued</option>
              <option value="partial">Partial</option>
              <option value="paid">Paid</option>
              <option value="overdue">Overdue</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} className="w-full h-11 border-gray-300 rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500" />
          </div>
          <div className="flex items-end">
            <button type="button" onClick={clearFilters} className="w-full h-11 px-4 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
              Clear filters
            </button>
          </div>
        </FilterPanel>

        {/* Invoice Table */}
        <div className={!loading && invoices.length > 0
          ? 'md:bg-white md:rounded-lg md:shadow md:overflow-hidden'
          : 'bg-white rounded-2xl border border-slate-200 md:border-0 md:rounded-lg md:shadow overflow-hidden'}>
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner />
            </div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
              <p className="text-gray-500 mb-4">Create your first invoice to get started.</p>
              <button
                onClick={handleCreateInvoice}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Create Invoice
              </button>
            </div>
          ) : (
            <InvoiceTable
              invoices={invoices}
              onView={handleViewInvoice}
              onEdit={handleEditInvoice}
              onDelete={handleDeleteInvoice}
              getStatusBadgeColor={getStatusBadgeColor}
            />
          )}
        </div>

        {/* Create/Edit Invoice Modal */}
        <Modal
          isOpen={showFormModal}
          onClose={() => {
            setShowFormModal(false);
            setEditInvoice(null);
          }}
          title={editInvoice ? 'Edit Invoice' : 'New Invoice'}
          size="xl"
        >
          <InvoiceForm
            invoice={editInvoice}
            onSave={handleInvoiceSaved}
            onCancel={() => {
              setShowFormModal(false);
              setEditInvoice(null);
            }}
          />
        </Modal>

        {/* Invoice Detail Modal */}
        {selectedInvoice && (
          <Modal
            isOpen={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setSelectedInvoice(null);
            }}
            title={`Invoice ${selectedInvoice.invoiceNumber}`}
            size="xl"
          >
            <InvoiceDetail
              invoice={selectedInvoice}
              onStatusChange={handleStatusChange}
              onEdit={() => {
                setShowDetailModal(false);
                handleEditInvoice(selectedInvoice);
              }}
              getStatusBadgeColor={getStatusBadgeColor}
            />
          </Modal>
        )}
      </div>
      {confirmDialog}
    </DashboardLayout>
  );
};

export default Invoices;
